/**
 * Toast Notification Component
 *
 * Beautiful, animated toast notifications for real-time updates.
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  X,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// Types
// =============================================================================

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'decision';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// =============================================================================
// Toast Store (simple state management)
// =============================================================================

type ToastListener = (toasts: Toast[]) => void;

class ToastStore {
  private toasts: Toast[] = [];
  private listeners: Set<ToastListener> = new Set();

  subscribe(listener: ToastListener): () => void {
    this.listeners.add(listener);
    listener(this.toasts);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener(this.toasts));
  }

  add(toast: Omit<Toast, 'id'>): string {
    const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const newToast: Toast = { ...toast, id, duration: toast.duration ?? 5000 };

    this.toasts = [...this.toasts, newToast];
    this.notify();

    // Auto remove
    if (newToast.duration > 0) {
      setTimeout(() => this.remove(id), newToast.duration);
    }

    return id;
  }

  remove(id: string): void {
    this.toasts = this.toasts.filter((t) => t.id !== id);
    this.notify();
  }

  clear(): void {
    this.toasts = [];
    this.notify();
  }
}

const toastStore = new ToastStore();

// =============================================================================
// Toast Functions
// =============================================================================

export const toast = {
  success: (title: string, message?: string) =>
    toastStore.add({ type: 'success', title, message }),

  error: (title: string, message?: string) =>
    toastStore.add({ type: 'error', title, message }),

  warning: (title: string, message?: string) =>
    toastStore.add({ type: 'warning', title, message }),

  info: (title: string, message?: string) =>
    toastStore.add({ type: 'info', title, message }),

  decision: (title: string, message?: string, action?: Toast['action']) =>
    toastStore.add({ type: 'decision', title, message, action, duration: 8000 }),

  dismiss: (id: string) => toastStore.remove(id),

  clear: () => toastStore.clear(),
};

// =============================================================================
// Toast Icon
// =============================================================================

const toastIcons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle2 className="w-5 h-5" />,
  error: <XCircle className="w-5 h-5" />,
  warning: <AlertTriangle className="w-5 h-5" />,
  info: <Info className="w-5 h-5" />,
  decision: <Zap className="w-5 h-5" />,
};

const toastColors: Record<ToastType, string> = {
  success: 'bg-success-50 border-success-200 text-success-800 dark:bg-success-900/30 dark:border-success-800 dark:text-success-200',
  error: 'bg-danger-50 border-danger-200 text-danger-800 dark:bg-danger-900/30 dark:border-danger-800 dark:text-danger-200',
  warning: 'bg-warning-50 border-warning-200 text-warning-800 dark:bg-warning-900/30 dark:border-warning-800 dark:text-warning-200',
  info: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-200',
  decision: 'bg-accent-50 border-accent-200 text-accent-800 dark:bg-accent-900/30 dark:border-accent-800 dark:text-accent-200',
};

const toastIconColors: Record<ToastType, string> = {
  success: 'text-success-600 dark:text-success-400',
  error: 'text-danger-600 dark:text-danger-400',
  warning: 'text-warning-600 dark:text-warning-400',
  info: 'text-blue-600 dark:text-blue-400',
  decision: 'text-accent-600 dark:text-accent-400',
};

// =============================================================================
// Single Toast Component
// =============================================================================

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.95 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className={cn(
        'relative flex items-start gap-3 p-4 rounded-xl border shadow-soft-lg',
        'backdrop-blur-sm min-w-[320px] max-w-[420px]',
        toastColors[toast.type]
      )}
    >
      {/* Icon */}
      <div className={cn('flex-shrink-0 mt-0.5', toastIconColors[toast.type])}>
        {toastIcons[toast.type]}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{toast.title}</p>
        {toast.message && (
          <p className="text-sm opacity-80 mt-0.5">{toast.message}</p>
        )}
        {toast.action && (
          <button
            onClick={toast.action.onClick}
            className="mt-2 text-sm font-medium underline underline-offset-2 hover:opacity-80"
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={() => onDismiss(toast.id)}
        className="flex-shrink-0 p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
      >
        <X className="w-4 h-4 opacity-60" />
      </button>
    </motion.div>
  );
}

// =============================================================================
// Toast Container
// =============================================================================

export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    return toastStore.subscribe(setToasts);
  }, []);

  const handleDismiss = useCallback((id: string) => {
    toastStore.remove(id);
  }, []);

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
      <AnimatePresence mode="popLayout">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={handleDismiss} />
        ))}
      </AnimatePresence>
    </div>
  );
}

export default toast;
